import React, { useEffect } from 'react';
import { Spin } from 'antd';
import { SearchOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { libellesTraduits, avecLibelleTraduit } from '../../i18n/libelles';
import { destinationRoute } from './panneauRecherche';

/**
 * Palette de recherche : le panneau centre qui remplace le menu deroulant.
 *
 * POURQUOI UNE PALETTE PLUTOT QU'UN DEROULANT. Le champ de la navbar fait
 * 360 px et son menu ne s'ouvrait qu'a partir de trois caracteres : cliquer
 * dessus n'affichait RIEN. L'agent clique, ne voit rien, et conclut que la
 * recherche ne marche pas, alors qu'elle attend seulement sa troisieme lettre.
 * La palette s'ouvre au clic, immediatement, avec de quoi lire.
 *
 * CE COMPOSANT NE PORTE AUCUNE LOGIQUE DE RECHERCHE, ET C'EST VOULU. La
 * temporisation, l'abandon de requete, la garde de sequence et le classement
 * clavier restent dans la navbar, ou trois rondes de revue les ont corriges.
 * Un changement d'habillage n'est pas une raison de reecrire les pieces
 * difficiles : tout arrive ici par des props deja calculees.
 */

/**
 * Ce que la recherche couvre, par module, pour l'afficher avant la premiere
 * frappe. Sans cette ligne, la palette ouverte sur un champ vide ne dit pas ce
 * qu'elle sait faire, et l'agent tape au hasard.
 *
 * Les cles sont celles que rend `moduleDeRoute`. Un module absent, ou `null`
 * sur un ecran non declare, tombe sur le texte general : c'est la meme
 * degradation que cote serveur, ou un module inconnu rouvre la recherche a
 * tous les types.
 */
const COUVERTURE_PAR_MODULE = libellesTraduits('commun:paletteRecherche.couvertureParModule');

const COULEURS_MENTION = avecLibelleTraduit({
  erreur: { fond: '#fef2f2', etiquette: '#b91c1c', texte: '#7f1d1d' },
  demander: { fond: '#fffbeb', etiquette: '#b45309', texte: '#92400e' },
  reessayer: { fond: '#f9fafb', etiquette: '#4b5563', texte: '#374151' },
  plafond: { fond: '#f9fafb', etiquette: '#4b5563', texte: '#374151' },
}, 'commun:paletteRecherche.mentions', { champ: 'libelle' });

const S = {
  voile: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
    zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
    padding: '12vh 16px 16px',
  },
  palette: {
    width: '100%', maxWidth: 640, background: '#fff', borderRadius: 12,
    boxShadow: '0 24px 64px rgba(0,0,0,0.28)', overflow: 'hidden',
    display: 'flex', flexDirection: 'column', maxHeight: '72vh',
  },
  champ: {
    flex: 1, height: 52, border: 'none', outline: 'none',
    fontSize: 16, fontFamily: "'DM Sans', sans-serif", color: '#111827',
    background: 'transparent',
  },
  enTeteGroupe: {
    padding: '8px 16px 4px', fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
    color: '#1a6b3c', textTransform: 'uppercase', background: '#fafafa',
  },
  ligneVide: { padding: '20px 16px', color: '#6b7280', fontSize: 13, textAlign: 'center' },
  piedAide: {
    padding: '8px 16px', borderTop: '1px solid #f0f0f0', background: '#fafafa',
    fontSize: 11, color: '#9ca3af', display: 'flex', gap: 16, flexWrap: 'wrap',
  },
};

function Touche({ children }) {
  return (
    <span style={{
      border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 5px',
      background: '#fff', fontFamily: 'monospace', fontSize: 10, color: '#4b5563',
    }}
    >
      {children}
    </span>
  );
}

export default function PaletteRecherche({
  ouverte, onFermer,
  valeur, onChangerValeur, onToucheChamp, champRef,
  enCours, groupes, aplati, indexSurligne, onSurligner, onChoisir,
  mentions, detailsAccesOuverts, onBasculerDetailsAcces,
  recentes, onChoisirRecente, onEffacerRecentes,
  moduleCourant, longueurMinimale, idListe,
}) {
  const { t } = useTranslation('commun');
  // Le focus part dans le champ des l'ouverture : la palette est appelee au
  // clavier par Ctrl+K aussi bien qu'a la souris, et obliger a cliquer dans le
  // champ apres un raccourci clavier annulerait l'interet du raccourci.
  useEffect(() => {
    if (ouverte) champRef?.current?.focus();
  }, [ouverte, champRef]);

  // Le fond de page ne defile plus tant que la palette est ouverte : sans
  // cela, la molette fait glisser l'ecran derriere le voile et l'agent perd
  // le contexte qu'il vient de quitter.
  useEffect(() => {
    if (!ouverte) return undefined;
    const precedent = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = precedent; };
  }, [ouverte]);

  if (!ouverte) return null;

  // PIEGE DE FOCUS. `aria-modal="true"` promet a un lecteur d'ecran que rien
  // d'autre n'est atteignable ; sans ce piege, Tab sort quand meme de la
  // palette et l'agent se retrouve a tabuler dans une page qu'il ne voit plus,
  // derriere le voile. La promesse serait fausse precisement pour les
  // utilisateurs qui s'y fient.
  const surToucheDialogue = (e) => {
    if (e.key !== 'Tab') return;
    const focalisables = e.currentTarget.querySelectorAll(
      'input, button, [tabindex]:not([tabindex="-1"])',
    );
    if (focalisables.length === 0) return;
    const premier = focalisables[0];
    const dernier = focalisables[focalisables.length - 1];
    // On ne detourne que les deux bords : entre eux, le parcours naturel du
    // navigateur est deja le bon, et le reecrire introduirait des surprises.
    if (e.shiftKey && document.activeElement === premier) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && document.activeElement === dernier) {
      e.preventDefault();
      premier.focus();
    }
  };

  const terme = (valeur || '').trim();
  const tropCourt = terme.length > 0 && terme.length < longueurMinimale;
  const avantFrappe = terme.length === 0;
  const aDesResultats = aplati.length > 0;

  return (
    // Le clic sur le voile ferme, comme partout ailleurs. mouseDown plutot que
    // click : un glisser commence dans la liste et relache sur le voile ne
    // doit pas fermer la palette par surprise.
    <div
      style={S.voile}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onFermer(); }}
      role="presentation"
    >
      <div
        style={S.palette}
        role="dialog"
        aria-modal="true"
        aria-label={t('paletteRecherche.rechercheGlobale')}
        onKeyDown={surToucheDialogue}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0 14px', borderBottom: '1px solid #f0f0f0',
        }}
        >
          <SearchOutlined style={{ color: '#9ca3af', fontSize: 16 }} />
          <input
            ref={champRef}
            role="combobox"
            aria-expanded={aDesResultats}
            aria-controls={idListe}
            aria-autocomplete="list"
            aria-activedescendant={indexSurligne >= 0 ? `${idListe}-option-${indexSurligne}` : undefined}
            value={valeur}
            onChange={(e) => onChangerValeur(e.target.value)}
            onKeyDown={onToucheChamp}
            placeholder={t('paletteRecherche.placeholder')}
            aria-label={t('paletteRecherche.rechercher')}
            style={S.champ}
          />
          {enCours && <Spin size="small" />}
          <button
            type="button"
            onClick={onFermer}
            aria-label={t('paletteRecherche.fermer')}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer',
              color: '#9ca3af', padding: 4, lineHeight: 1,
            }}
          >
            <CloseOutlined />
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {/* Champ vide : on montre ce qu'on a plutot qu'un panneau blanc. Les
              recentes d'abord, parce que c'est ce que l'agent vient rechercher
              le plus souvent, puis ce que la recherche couvre ici. */}
          {avantFrappe && (
            <>
              {recentes.length > 0 && (
                <>
                  <div style={{ ...S.enTeteGroupe, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{t('paletteRecherche.recentes')}</span>
                    <button
                      type="button"
                      onClick={onEffacerRecentes}
                      style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        color: '#9ca3af', fontSize: 10, textTransform: 'none', letterSpacing: 0,
                      }}
                    >
                      {t('paletteRecherche.effacer')}
                    </button>
                  </div>
                  {recentes.map((t) => (
                    <div
                      key={t}
                      onClick={() => onChoisirRecente(t)}
                      onKeyDown={(e) => { if (e.key === 'Enter') onChoisirRecente(t); }}
                      role="button"
                      tabIndex={0}
                      style={{
                        padding: '9px 16px', cursor: 'pointer', fontSize: 13, color: '#374151',
                        display: 'flex', alignItems: 'center', gap: 8,
                      }}
                    >
                      <SearchOutlined style={{ color: '#d1d5db', fontSize: 12 }} />
                      {t}
                    </div>
                  ))}
                </>
              )}
              <div style={{ padding: '14px 16px', fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
                {t('paletteRecherche.couvreLes', { couverture: COUVERTURE_PAR_MODULE[moduleCourant] || t('paletteRecherche.couvertureGenerale') })}
                <br />
                <span style={{ color: '#9ca3af' }}>
                  {t('paletteRecherche.tapezAuMoins', { nombre: longueurMinimale })}
                </span>
              </div>
            </>
          )}

          {tropCourt && (
            <div style={S.ligneVide}>
              {t('paletteRecherche.encoreCaracteres', { count: longueurMinimale - terme.length })}
            </div>
          )}

          {/* Tant que la recherche tourne, on ne montre QUE le chargement :
              sinon les resultats de la frappe precedente restent affiches et
              l'agent les prend pour la reponse de sa frappe actuelle. */}
          {!avantFrappe && !tropCourt && enCours && (
            <div style={{ padding: 28, textAlign: 'center' }}><Spin /></div>
          )}

          {!avantFrappe && !tropCourt && !enCours && (
            <>
              {!aDesResultats && mentions.length === 0 && (
                <div style={S.ligneVide}>{t('paletteRecherche.aucunResultat')}</div>
              )}

              <div id={idListe} role="listbox" aria-label={t('paletteRecherche.resultats')}>
                {groupes.map((groupe) => (
                  <div key={groupe.type}>
                    <div style={S.enTeteGroupe}>
                      {groupe.type}
                      {groupe.tronque && (
                        <span style={{ fontWeight: 500, textTransform: 'none', color: '#9ca3af' }}>
                          {' '}
                          {t('paletteRecherche.affichageLimite')}
                        </span>
                      )}
                    </div>
                    {groupe.resultats.map((r) => {
                      // L'index est celui de `aplati`, la liste dans l'ordre ou
                      // l'agent lit les lignes : c'est la seule liste que la
                      // surbrillance clavier a le droit d'indexer.
                      const i = aplati.indexOf(r);
                      return (
                        <div
                          key={`${r.type}-${r.route}-${i}`}
                          id={`${idListe}-option-${i}`}
                          role="option"
                          aria-selected={i === indexSurligne}
                          onClick={() => onChoisir(r)}
                          onMouseEnter={() => onSurligner(i)}
                          style={{
                            padding: '9px 16px', cursor: 'pointer',
                            background: i === indexSurligne ? '#f0fdf4' : 'transparent',
                            borderLeft: i === indexSurligne ? '3px solid #1a6b3c' : '3px solid transparent',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{r.titre}</div>
                          {/* Une seule ligne, ellipse : un detail long (l'objet
                              d'un courrier) ne doit pas faire varier la hauteur
                              d'une ligne a l'autre. */}
                          {r.detail && (
                            <div style={{
                              fontSize: 12, color: '#6b7280',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                            >
                              {r.detail}
                            </div>
                          )}
                          {/* Cinq types sur huit n'ont pas de fiche : le clic
                              depose sur la liste du type, non filtree par ce
                              numero. Sans cette ligne, l'agent croit que le
                              clic a rate. */}
                          {destinationRoute(r) === 'liste' && (
                            <div style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
                              {t('paletteRecherche.pasDeFiche')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Quatre tons, quatre gestes. 'demander' est replie par defaut :
                  un agent peu habilite a sept types hors de ses droits a chaque
                  frappe, et les empiler ecrasait le seul resultat auquel il a
                  droit. aria-live, sinon un lecteur d'ecran n'annoncerait
                  jamais « vous n'avez pas acces aux PV ». */}
              {mentions.length > 0 && (
                <div role="status" aria-live="polite" style={{ borderTop: '1px solid #f0f0f0' }}>
                  {mentions.map((m) => {
                    const c = COULEURS_MENTION[m.ton] || COULEURS_MENTION.reessayer;
                    const repliable = Array.isArray(m.details) && m.details.length > 0;
                    return (
                      <div key={m.ton} style={{ background: c.fond, padding: '8px 16px' }}>
                        <div
                          onClick={repliable ? onBasculerDetailsAcces : undefined}
                          onKeyDown={repliable ? (e) => { if (e.key === 'Enter') onBasculerDetailsAcces(); } : undefined}
                          role={repliable ? 'button' : undefined}
                          tabIndex={repliable ? 0 : undefined}
                          aria-expanded={repliable ? detailsAccesOuverts : undefined}
                          style={{
                            display: 'flex', gap: 8, alignItems: 'baseline',
                            cursor: repliable ? 'pointer' : 'default', fontSize: 12, color: c.texte,
                          }}
                        >
                          <span style={{ fontSize: 10, fontWeight: 700, color: c.etiquette, textTransform: 'uppercase' }}>
                            {c.libelle}
                          </span>
                          <span>{m.texte}</span>
                          {repliable && <span style={{ color: c.etiquette }}>{detailsAccesOuverts ? '▴' : '▾'}</span>}
                        </div>
                        {repliable && detailsAccesOuverts && (
                          <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12, color: c.texte }}>
                            {m.details.map((d) => (
                              <li key={d.type}>
                                {d.type}
                                {' '}
                                <span style={{ color: c.etiquette }}>
                                  (
                                  {d.habilitationRequise}
                                  )
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <div style={S.piedAide}>
          <span>
            <Touche>↑</Touche>
            {' '}
            <Touche>↓</Touche>
            {' '}
            {t('paletteRecherche.naviguer')}
          </span>
          <span>
            <Touche>{t('paletteRecherche.toucheEntree')}</Touche>
            {' '}
            {t('paletteRecherche.ouvrir')}
          </span>
          <span>
            <Touche>{t('paletteRecherche.toucheEchap')}</Touche>
            {' '}
            {t('paletteRecherche.fermerAide')}
          </span>
        </div>
      </div>
    </div>
  );
}
